/**
 * B190 fix2 — device-local residue cleared after a self-serve account deletion.
 *
 * Security review (harvest b190, finding 4): "This deletes your account, your
 * plan, your runs and your history" was true server-side but nothing cleared
 * the same content cached on the device. On a shared browser it survived a
 * deletion the UI promised removed it.
 *
 * `clearOnAccountDeletion()` removes ONLY keys this app itself writes. It
 * never calls `localStorage.clear()` — a shared browser may hold keys this
 * app does not own. Every removal is independently wrapped so one failure
 * never stops the rest.
 *
 * `lib/run/saveQueueStore.ts`'s `tayro.runSaveQueue.v1` is DEVICE-GLOBAL and
 * holds OTHER accounts' still-unsynced queued runs on the same device (B56)
 * — so this never wipes that key. B190 fix4 instead calls the store's
 * OWNER-SCOPED mutator, `removeQueuedRunsForUser(userId)`, which drops only
 * this account's entries and leaves every other account's queued run exactly
 * as it is. Likewise `lib/nativePush.ts`'s `tayro_push_installation_id` is a
 * device identifier, not this account's data, and is left alone.
 *
 * THE INVENTORY (one line per real writer this DOES clear):
 *   - `lib/conversations.ts`      → `wai_sports_chat_<userId>_<coachId>`
 *                                    (per-coach; no fixed coach list this
 *                                    module owns, so cleared by userId prefix)
 *   - `hooks/onboardingReducer.ts` → `STORAGE_KEY` ('wai:onboarding:v2:preview')
 *   - `components/onboarding/forkStorage.ts` → `FORK_STORAGE_KEY`
 *   - `lib/health/importConsent.ts` → `LEGACY_IMPORT_ENABLED_KEY` +
 *                                    `importConsentStorageKey(userId)`
 *   - `lib/boot/bootTier.ts` (Capacitor Preferences, native only) →
 *                                    `BOOT_LAST_ACTIVE_KEY`
 *   - `lib/run/saveQueueStore.ts` (B190 fix4) → owner-scoped removal of this
 *                                    account's `QueuedRun` entries only
 *   - `lib/userFlagState.ts` (B190 fix4) → this id forgotten from every
 *                                    `wai:b76:*` once-only ledger
 *   - `lib/appleAuth.ts` (B190 fix4) → `APPLE_NAME_STASH_KEY`, device-global
 *   - `lib/appleNameSeed.ts` (B190 fix4) → `APPLE_NAME_SEED_KEY`, device-global
 *   - `lib/map/raceLiveMapView.ts` (B190 fix4) → `normsAckStorageKey(userId)`
 *                                    (derivable from the id alone, no season)
 *   - `lib/accountView.ts` (W2-CHROME, CH-10) → `ACCOUNT_VIEW_KEY`
 *                                    (`tayro.accountView.v1`), the remembered
 *                                    View choice, a single device-global slot
 *                                    stamped with a user id. Sign out leaves
 *                                    it (remembered per device); a deletion
 *                                    removes it whoever the slot names.
 *   - `lib/todayLayoutMemory.ts` (W1-TODAY-LAYOUT-MEMORY) → `TODAY_LAYOUT_KEY`
 *                                    (`tayro.todayLayout.v1`), the block set Today's
 *                                    loading frame remembers: one device-global slot
 *                                    stamped with a user id. Sign out clears it too
 *                                    (`contexts/AuthContext`); a deletion removes it
 *                                    whoever the slot names.
 *   - `lib/settingsPlanShape.ts` (B481 fix round 1) → `PLAN_SHAPE_KEY`
 *                                    (`tayro.settingsPlan.v1`), the plan card's last-known shape (a word)
 *                                    and whether the Guardians row drew (a boolean): one device-global
 *                                    slot stamped with a user id. Sign out clears it too
 *                                    (`contexts/AuthContext`); a deletion removes it whoever the slot names.
 *   - `routes/native-welcome.tsx` (B445) → `HAS_SIGNED_IN_KEY`
 *                                    (`tayro.hasSignedIn`), device-global: left in place it
 *                                    made the native welcome greet a deleted account with
 *                                    "WELCOME BACK". Sign out keeps it (a returning user).
 *
 * NOT cleared, and why (B190 fix4 residue inventory, review finding 4):
 *   - `lib/reanchor.ts`'s `reanchorDismissKey(seasonId, signal)` needs a
 *     season id and a meet id/date this handler never has at deletion time,
 *     and `lib/reanchor.ts` is outside this lane's ownership — left as a
 *     named remaining item in the lane report, not solved here.
 *
 * `deviceState.test.ts` writes through each of these real writers, calls
 * this clearer, and asserts every key it wrote is gone.
 */

import { Preferences } from '@capacitor/preferences';
import { isNativeApp } from './platform';
import { STORAGE_KEY as ONBOARDING_WIZARD_KEY } from '../hooks/onboardingReducer';
import { FORK_STORAGE_KEY } from '../components/onboarding/forkStorage';
import { LEGACY_IMPORT_ENABLED_KEY, importConsentStorageKey } from './health/importConsent';
import { BOOT_LAST_ACTIVE_KEY } from './boot/bootTier';
import { removeQueuedRunsForUser } from './run/saveQueueStore';
import { forgetUser } from './userFlagState';
import { APPLE_NAME_STASH_KEY } from './appleAuth';
import { APPLE_NAME_SEED_KEY } from './appleNameSeed';
import { normsAckStorageKey } from './map/raceLiveMapView';
import { clearStoredAccountView } from './accountView';
import { TODAY_LAYOUT_KEY } from './todayLayoutMemory';
import { PLAN_SHAPE_KEY } from './settingsPlanShape';
import { HAS_SIGNED_IN_KEY } from '../routes/native-welcome';

/** `lib/conversations.ts`'s `localStorageKey(userId, coachId)` prefix — that
 *  module has no exported constant for it (the template literal is inline),
 *  so this is a literal pin. `deviceState.test.ts` writes through the real
 *  `saveChatData` and proves this prefix still matches. */
const CHAT_HISTORY_PREFIX = 'wai_sports_chat_';

async function removeLocalStorageKey(key: string): Promise<void> {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* private mode / storage disabled — best effort */
  }
}

/** Every `wai_sports_chat_<userId>_*` key, one per coach the user chatted
 *  with. Scanned rather than enumerated — this module owns no coach list. */
async function removeChatHistory(userId: string): Promise<void> {
  try {
    const prefix = `${CHAT_HISTORY_PREFIX}${userId}_`;
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    /* private mode / storage disabled — best effort */
  }
}

/**
 * Removes every device-local key this app itself wrote for `userId`, called
 * once the server has confirmed the account deletion (a 2xx), never before.
 *
 * @param userId the deleted account's id, or `null` if it was never known —
 *   the user-scoped removals become no-ops (nothing to have been keyed
 *   under), the device-wide ones still run.
 */
export async function clearOnAccountDeletion(userId: string | null): Promise<void> {
  const removals: Array<() => Promise<void>> = [
    () => removeLocalStorageKey(ONBOARDING_WIZARD_KEY),
    () => removeLocalStorageKey(FORK_STORAGE_KEY),
    () => removeLocalStorageKey(LEGACY_IMPORT_ENABLED_KEY),
    // B190 fix4 — both device-global, single-slot keys; removed regardless of
    // which account is mid-deletion (the stash is keyed by Apple's `sub`, not
    // by this Supabase user id, so there is no id to scope the removal to).
    () => removeLocalStorageKey(APPLE_NAME_STASH_KEY),
    () => removeLocalStorageKey(APPLE_NAME_SEED_KEY),
    // W2-CHROME CH-10 — the remembered View, one device-global slot. Through
    // the module's own remover so the in-page memory mirror goes with it.
    () => Promise.resolve(clearStoredAccountView()),
    // W1-TODAY-LAYOUT-MEMORY — the block set Today's loading frame remembers, one device-global
    // slot stamped with a user id (sign-out clears it too, in `contexts/AuthContext`).
    () => removeLocalStorageKey(TODAY_LAYOUT_KEY),
    // B481 fix round 1 - the plan card's last-known shape and whether the Guardians row drew, one
    // device-global slot stamped with a user id (sign-out clears it too, in `contexts/AuthContext`).
    () => removeLocalStorageKey(PLAN_SHAPE_KEY),
    // B445 — the "a session was seen on this device" marker, one device-global slot.
    () => removeLocalStorageKey(HAS_SIGNED_IN_KEY),
  ];
  if (userId) {
    removals.push(() => removeChatHistory(userId));
    removals.push(() => removeLocalStorageKey(importConsentStorageKey(userId)));
    // B190 fix4 — owner-scoped: drops only this account's queued runs, never
    // another account's still-unsynced entry on the same device (B56).
    removals.push(() => removeQueuedRunsForUser(userId));
    // B190 fix4 — forgets this id from every `wai:b76:*` once-only ledger.
    removals.push(() => Promise.resolve(forgetUser(userId)));
    // B190 fix4 — the coach norms-acknowledgment gate re-shows, device-scoped
    // by userId alone (no season id needed to derive this key).
    removals.push(() => removeLocalStorageKey(normsAckStorageKey(userId)));
  }

  for (const remove of removals) {
    try {
      await remove();
    } catch {
      /* one failure never stops the rest */
    }
  }

  if (isNativeApp()) {
    try {
      await Preferences.remove({ key: BOOT_LAST_ACTIVE_KEY });
    } catch {
      /* best effort — Preferences unavailable */
    }
  }
}
